import { useState } from 'react'
import { format, isValid, parseISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import api from '@/http/api'
import { toast } from 'sonner'
import { Truck, Trash2, Plus, Copy, LoaderCircle, X, Pencil, Search } from 'lucide-react'
import { Button } from '@univdiam/ui/components/button'
import { Input } from '@univdiam/ui/components/input'
import { Label } from '@univdiam/ui/components/label'
import { AutocompleteSelect } from '@univdiam/ui/customComponents/AutocompleteSelect'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@univdiam/ui/components/table'
import { options as statusOptions, statusChipStyle, statusLabel } from '@univdiam/ui/lib/StatusOptions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@univdiam/ui/components/alert-dialog'
import Drawer from '@univdiam/ui/customComponents/Drawer'
import ErrorComponent from '@univdiam/ui/customComponents/ErrorComponent'
import { useOrderTracking, type OrderTracking, type OrderTrackingOrder } from '@/sharedData/OrderTracking'
import { useOrder } from '@/sharedData/Order'
import type { OrderType } from '@/types'

type TrackingPayload = {
  order_refs: string[]
  carrier: string
  tracking_id: string
  status: string
  shipping_date: string
}

const addTracking = (data: TrackingPayload) => api.post('/admin/order-tracking', data)
const editTracking = ({ id, ...data }: TrackingPayload & { id: number }) =>
  api.patch(`/admin/order-tracking/${id}`, data)
const removeTracking = (id: number) => api.delete(`/admin/order-tracking/${id}`)

// The tracking status reuses the order status vocabulary, stored as the human
// `value` rather than the id so a free-text entry can share the same column.
const statusPickerOptions = statusOptions.map(option => ({ value: option.value, label: option.label }))

// Known carrier tracking-page URL templates, keyed by a normalized carrier name.
// Anything else falls back to a Google search for the carrier + tracking ID.
const CARRIER_TRACKING_URLS: Record<string, (id: string) => string> = {
  ups: id => `https://www.ups.com/track?tracknum=${id}`,
  fedex: id => `https://www.fedex.com/fedextrack/?trknbr=${id}`,
  usps: id => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${id}`,
  dhl: id => `https://www.dhl.com/en/express/tracking.html?AWB=${id}`,
}

const getTrackingUrl = (carrier: string, trackingId: string) => {
  const pasted = trackingId.trim()
  if (/^https?:\/\//i.test(pasted)) return pasted
  const key = carrier.trim().toLowerCase()
  const build = CARRIER_TRACKING_URLS[key]
  if (build) return build(encodeURIComponent(trackingId))
  return `https://www.google.com/search?q=${encodeURIComponent(`${carrier} tracking ${trackingId}`)}`
}

// Pasted links read as noise in a narrow column; the host and path carry enough.
const trackingLabel = (trackingId: string) =>
  trackingId.trim().replace(/^https?:\/\/(www\.)?/i, '')

// Stored as a bare 'YYYY-MM-DD'. Only the date part is ever parsed, so the day
// cannot slide backwards for a viewer behind UTC.
const formatShippingDate = (value: string) => {
  const parsed = parseISO(value.split('T')[0]!)
  return isValid(parsed) ? format(parsed, 'dd MMM yyyy') : ''
}

// Display spellings for the carriers `CARRIER_TRACKING_URLS` already knows.
const CARRIER_PRESETS = ['FedEx', 'UPS', 'DHL', 'USPS']

export default function TrackOrder() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: tracking = [], isLoading } = useOrderTracking()
  const { data: ordersRes } = useOrder('', 1, 500)
  const orders = (((ordersRes as { data?: { data?: OrderType[] } } | undefined)?.data?.data) ?? []) as OrderType[]
  const orderOptions = orders.map(order => ({
    value: order.id.toString(),
    label: `${order.order_id} — ${order.c_business_name || order.c_name || 'Unnamed'}`
  }))
  // Tracking rows only carry order ids, so the customer business name comes from
  // the order list already loaded for the picker.
  const businessById = new Map(
    orders.map(order => [order.id.toString(), order.c_business_name || order.c_name || ''])
  )

  const [search, setSearch] = useState('')
  const [panelOpen, setPanelOpen] = useState(false)
  const [orderPickerValue, setOrderPickerValue] = useState('')
  const [selectedOrders, setSelectedOrders] = useState<string[]>([])
  const [carrier, setCarrier] = useState('')
  const [trackingId, setTrackingId] = useState('')
  const [status, setStatus] = useState('')
  const [shippingDate, setShippingDate] = useState('')
  const [error, setError] = useState('')
  const [pendingDelete, setPendingDelete] = useState<OrderTracking | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [statusEditId, setStatusEditId] = useState<number | null>(null)

  const addOrder = (value: string) => {
    setSelectedOrders(prev => (prev.includes(value) ? prev : [...prev, value]))
    setOrderPickerValue('')
    setError('')
  }

  const removeOrder = (value: string) => {
    setSelectedOrders(prev => prev.filter(v => v !== value))
  }

  const resetForm = () => {
    setPanelOpen(false)
    setEditingId(null)
    setSelectedOrders([])
    setOrderPickerValue('')
    setCarrier('')
    setTrackingId('')
    setStatus('')
    setShippingDate('')
    setError('')
  }

  const startEdit = (row: OrderTracking) => {
    setPanelOpen(true)
    setEditingId(row.id)
    setCarrier(row.carrier)
    setTrackingId(row.tracking_id)
    setStatus(row.status ?? '')
    setShippingDate(row.shipping_date?.split('T')[0] ?? '')
    setSelectedOrders(row.orders.map(order => order.order_id.toString()))
    setOrderPickerValue('')
    setError('')
  }

  const handleMutationError = (label: string) => (err: Error) => {
    const msg =
      (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
      label
    setError(msg)
    toast.error(msg)
  }

  const createMutation = useMutation({
    mutationFn: addTracking,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tracking'] })
      resetForm()
      toast.success('Tracking added.')
    },
    onError: handleMutationError('Failed to add tracking.'),
  })

  const updateMutation = useMutation({
    mutationFn: editTracking,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tracking'] })
      resetForm()
      toast.success('Tracking updated.')
    },
    onError: handleMutationError('Failed to update tracking.'),
  })

  // Inline status edit reuses the same PATCH, resending the row's own values so
  // the side form (which may be mid-edit of another row) is left alone.
  const statusMutation = useMutation({
    mutationFn: editTracking,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tracking'] })
      setStatusEditId(null)
      toast.success('Status updated.')
    },
    onError: () => {
      setStatusEditId(null)
      toast.error('Failed to update status.')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: removeTracking,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-tracking'] })
      setPendingDelete(null)
      toast.success('Tracking deleted.')
    },
    onError: () => toast.error('Failed to delete tracking.'),
  })

  const handleSubmit = () => {
    if (selectedOrders.length === 0 || !carrier.trim() || !trackingId.trim()) {
      setError('At least one order, carrier and tracking ID are all required.')
      return
    }
    setError('')
    const payload = {
      order_refs: selectedOrders,
      carrier: carrier.trim(),
      tracking_id: trackingId.trim(),
      status: status.trim(),
      shipping_date: shippingDate,
    }
    if (editingId) updateMutation.mutate({ id: editingId, ...payload })
    else createMutation.mutate(payload)
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const commitStatus = (row: OrderTracking, next: string) => {
    if (next.trim() === (row.status ?? '')) {
      setStatusEditId(null)
      return
    }
    statusMutation.mutate({
      id: row.id,
      order_refs: row.orders.map(order => order.order_id.toString()),
      carrier: row.carrier,
      tracking_id: row.tracking_id,
      status: next.trim(),
      shipping_date: row.shipping_date?.split('T')[0] ?? '',
    })
  }

  const confirmDelete = () => {
    if (!pendingDelete) return
    deleteMutation.mutate(pendingDelete.id)
  }

  const goToOrder = (order: OrderTrackingOrder) => {
    navigate(`/order/${order.order_id}/${order.order_ref}/${order.customer_id}`)
  }

  const query = search.trim().toLowerCase()
  const filtered = query
    ? tracking.filter(row =>
        [
          row.carrier,
          row.tracking_id,
          row.status ?? '',
          ...row.orders.flatMap(order => [
            order.order_ref,
            order.job_title ?? '',
            businessById.get(order.order_id.toString()) ?? '',
          ]),
        ].some(field => field.toLowerCase().includes(query))
      )
    : tracking

  const copyTrackingId = (trackingId: string) => {
    navigator.clipboard.writeText(trackingId)
    toast.success('Tracking ID copied.')
  }

  return (
    <div className="mx-auto max-w-content p-6">
      <div>
        {/* ── Tracking list ── */}
        <div className="overflow-clip rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
            <div className="flex items-baseline gap-2">
              <Truck size={15} strokeWidth={1.5} className="shrink-0 translate-y-0.5 text-muted-foreground" />
              <h2 className="text-body2 font-semibold text-foreground">Track Order</h2>
              <span className="text-caption2 text-muted-foreground tabular-nums">
                {query ? `${filtered.length} of ${tracking.length}` : tracking.length}
              </span>
            </div>
            <div className="flex flex-1 items-center justify-end gap-2">
              <div className="relative w-full max-w-64">
                <Search size={14} className="absolute top-1/2 left-2.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search carrier, tracking, order…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="h-8 pl-8 text-body2"
                />
              </div>
              <Button
                onClick={() => setPanelOpen(true)}
                className="h-8 shrink-0 gap-1.5 bg-[#883734] px-3 text-white hover:bg-[#883734]/90"
              >
                <Plus size={14} strokeWidth={2} />
                Add tracking
              </Button>
            </div>
          </div>

          <Table className="table-fixed [&_td]:align-top">
            <TableHeader className="bg-card">
              <TableRow className="[&_th]:text-caption2 [&_th]:font-medium [&_th]:tracking-wider [&_th]:text-muted-foreground [&_th]:uppercase">
                <TableHead className="w-[24%] px-6 py-3 text-left">Shipment</TableHead>
                <TableHead className="w-[18%] px-6 py-3 text-left">Status</TableHead>
                <TableHead className="w-[50%] px-6 py-3 text-left">Orders</TableHead>
                <TableHead className="w-[8%] px-6 py-3" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-28 text-center align-middle text-body2 text-muted-foreground">Loading…</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-28 text-center align-middle text-body2 text-muted-foreground">
                    {query ? `Nothing matches “${search.trim()}”.` : 'No tracking yet. Add a carrier and tracking number to get started.'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(row => (
                  <TableRow
                    key={row.id}
                    className={`group transition-colors ${
                      editingId === row.id ? 'bg-[#883734]/5' : 'bg-card hover:bg-muted/40'
                    }`}
                  >
                    {/* Carrier and tracking number are one fact -- the shipment -- so
                        they share a column: carrier labels, the number is the value. */}
                    <TableCell className="px-6 py-4">
                      <div className="flex h-6 items-center gap-0.5">
                        <a
                          href={getTrackingUrl(row.carrier, row.tracking_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={row.tracking_id}
                          className="truncate text-body2 tracking-wide tabular-nums text-foreground underline-offset-4 hover:text-[#883734] hover:underline"
                        >
                          {trackingLabel(row.tracking_id)}
                        </a>
                        <button
                          onClick={() => copyTrackingId(row.tracking_id)}
                          title="Copy tracking number"
                          className="shrink-0 rounded p-1 text-muted-foreground transition-opacity hover:text-foreground md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 text-caption2 leading-4 text-muted-foreground">
                        <span className="truncate">{row.carrier}</span>
                        {row.shipping_date && (
                          <>
                            <span className="shrink-0">·</span>
                            <span
                              className="shrink-0 tabular-nums"
                              title={`Shipped ${formatShippingDate(row.shipping_date)}`}
                            >
                              {formatShippingDate(row.shipping_date)}
                            </span>
                          </>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="px-6 py-4">
                      {statusEditId === row.id ? (
                        // AutocompleteSelect owns its popover state and does not
                        // report it back, so closing the list can't collapse the
                        // editor -- an explicit dismiss does.
                        <div className="-my-1.5 flex items-center gap-1">
                          <AutocompleteSelect
                            creatable
                            value={row.status ?? ''}
                            onChange={next => commitStatus(row, next)}
                            options={statusPickerOptions}
                            disabled={statusMutation.isPending}
                            placeholder="Set status"
                            searchPlaceholder="Search or type a status…"
                            emptyText="No matching status"
                            className="w-full"
                          />
                          <button
                            onClick={() => setStatusEditId(null)}
                            title="Done"
                            className="shrink-0 text-muted-foreground hover:text-foreground"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : row.status ? (
                        <button
                          onClick={() => setStatusEditId(row.id)}
                          title="Change status"
                          style={statusChipStyle(row.status)}
                          className="inline-flex h-6 w-fit max-w-full items-center rounded-[4px] border px-2 text-caption2 whitespace-nowrap text-black transition-opacity hover:opacity-80"
                        >
                          <span className="truncate">{statusLabel(row.status)}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => setStatusEditId(row.id)}
                          title="Set status"
                          className="inline-flex h-6 w-fit items-center rounded-[4px] border border-dashed border-input px-2 text-caption2 text-muted-foreground hover:border-solid hover:text-foreground"
                        >
                          Set status
                        </button>
                      )}
                    </TableCell>

                    {/* One block per order, whole block clickable -- the order ref sits
                        right-aligned so the refs form a single readable rail. */}
                    <TableCell className="px-6 py-4">
                      <div className="-my-1.5 flex flex-col">
                        {row.orders.map(order => {
                          const business = businessById.get(order.order_id.toString())
                          return (
                            <button
                              key={order.order_id}
                              onClick={() => goToOrder(order)}
                              title="Open order"
                              className="-mx-2.5 flex w-full flex-col rounded-md px-2.5 py-1.5 text-left transition-colors hover:bg-[#883734]/8"
                            >
                              <span className="flex h-6 w-full items-center gap-3">
                                <span className="min-w-0 truncate text-body2 font-medium text-foreground">
                                  {business || 'Unnamed customer'}
                                </span>
                                <span className="ml-auto shrink-0 text-caption2 tracking-wide tabular-nums text-muted-foreground">
                                  #{order.order_ref}
                                </span>
                              </span>
                              {order.job_title && (
                                <span className="mt-1 w-full truncate text-caption2 leading-4 text-muted-foreground">
                                  {order.job_title}
                                </span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </TableCell>

                    <TableCell className="px-6 py-4">
                      <div className="-my-0.5 flex h-7 items-center justify-end gap-0.5 transition-opacity md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(row)}
                          className="h-7 w-7 rounded-md p-0 text-muted-foreground hover:bg-[#883734]/10 hover:text-[#883734]"
                          title="Edit"
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPendingDelete(row)}
                          disabled={deleteMutation.isPending}
                          className="h-7 w-7 rounded-md p-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* ── Add / edit drawer ── */}
        <Drawer
          open={panelOpen}
          setOpen={open => { if (!open && !isSubmitting) resetForm() }}
          title={
            <p className="flex items-center gap-[8px]">
              <Truck size={18} strokeWidth={1.5} />
              {editingId ? 'Edit Tracking' : 'Add Tracking'}
            </p>
          }
        >
          <div className="pt-[16px]">
            <div className="mb-0 w-full">
              <Label className="text-[#38404D]">
                Carrier <span className="text-[#16111]">*</span>
              </Label>
              <div className="mt-[6px]">
                <Input
                  placeholder="FedEx, UPS, DHL…"
                  value={carrier}
                  onChange={e => { setCarrier(e.target.value); setError('') }}
                  className="h-[36px]"
                />
              </div>
              <div className="mt-[8px] flex flex-wrap gap-[8px]">
                {CARRIER_PRESETS.map(preset => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => { setCarrier(preset); setError('') }}
                    className={`rounded-full border px-[10px] py-[3px] text-caption2 transition-colors ${
                      carrier.trim().toLowerCase() === preset.toLowerCase()
                        ? 'border-[#883734]/30 bg-[#883734]/10 text-[#883734]'
                        : 'border-[#E9EAEB] text-[#777777] hover:text-[#38404D]'
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-0 mt-[16px] w-full">
              <Label className="text-[#38404D]">
                Tracking Number <span className="text-[#16111]">*</span>
              </Label>
              <div className="mt-[6px]">
                <Input
                  placeholder="1Z999AA10123456784"
                  value={trackingId}
                  onChange={e => { setTrackingId(e.target.value); setError('') }}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  className="h-[36px] tracking-wide tabular-nums"
                />
              </div>
            </div>

            <div className="mb-0 mt-[16px] w-full">
              <Label className="text-[#38404D]">Status</Label>
              <div className="mt-[6px] w-full">
                <AutocompleteSelect
                  creatable
                  value={status}
                  onChange={setStatus}
                  options={statusPickerOptions}
                  placeholder="Optional"
                  searchPlaceholder="Search or type a status…"
                  emptyText="No matching status"
                  className="w-full"
                />
              </div>
            </div>

            <div className="mb-0 mt-[16px] w-full">
              <Label className="text-[#38404D]">Shipping Date</Label>
              <div className="mt-[6px]">
                <Input
                  type="date"
                  value={shippingDate}
                  onChange={e => { setShippingDate(e.target.value); setError('') }}
                  className="h-[36px] w-[180px]"
                />
              </div>
            </div>

            <div className="mb-0 mt-[16px] w-full">
              <Label className="text-[#38404D]">
                Orders <span className="text-[#16111]">*</span>
              </Label>
              <p className="mt-[2px] text-caption2 text-[#777777]">
                Pick as many as this shipment covers — the list stays open.
              </p>

              {/* Chips sit above the picker: the popover opens downward, so
                  selections stay visible while several are being added. */}
              {selectedOrders.length > 0 && (
                <div className="mt-[8px] flex flex-wrap items-center gap-[8px]">
                  {selectedOrders.map(value => {
                    const option = orderOptions.find(o => o.value === value)
                    return (
                      <span
                        key={value}
                        title={option?.label ?? value}
                        className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#E9EAEB] bg-[#F9F9F9] py-1 pr-1 pl-2.5 text-caption2 text-[#38404D]"
                      >
                        <span className="truncate">{option?.label ?? value}</span>
                        <button
                          type="button"
                          onClick={() => removeOrder(value)}
                          className="rounded-full text-[#777777] hover:text-destructive"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )
                  })}
                </div>
              )}

              <div className="mt-[6px] w-full">
                <AutocompleteSelect
                  multiple
                  value={orderPickerValue}
                  onChange={addOrder}
                  options={orderOptions.filter(o => !selectedOrders.includes(o.value))}
                  placeholder={selectedOrders.length > 0 ? 'Add another order' : 'Add an order'}
                  searchPlaceholder="Search order ID or customer…"
                  emptyText="No matching orders"
                  className="w-full"
                />
              </div>
            </div>

            <div className="mt-[16px]">
              {error && <ErrorComponent message={error} />}
            </div>

            <div className="mt-[32px] mb-0 flex items-center gap-[16px]">
              <Button type="button" onClick={handleSubmit} className="w-fit" isLoading={isSubmitting}>
                {editingId ? 'Save Changes' : 'Add Tracking'}
              </Button>
            </div>
          </div>
        </Drawer>
      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setPendingDelete(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600/20"
              onClick={(e) => { e.preventDefault(); confirmDelete() }}
            >
              {deleteMutation.isPending ? <LoaderCircle className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
